import React, { createContext, useContext, useEffect, useState } from "react";
import api from "./api";

const BrandingCtx = createContext({
    institution_name: "Heritage Community College eLearning",
    tagline: "Learn Anywhere. Grow Everywhere.",
    logo_url: null,
    refresh: () => {},
});

// Always-available local SVG monogram. Served from /public/favicon.svg and
// guaranteed to render even when the uploaded PNG is missing or corrupted.
const LOCAL_MONOGRAM = "/favicon.svg";

export function BrandingProvider({ children }) {
    const [branding, setBranding] = useState({
        institution_name: "Heritage Community College eLearning",
        tagline: "Learn Anywhere. Grow Everywhere.",
        logo_url: LOCAL_MONOGRAM,
    });

    const refresh = async () => {
        try {
            const { data } = await api.get("/branding");
            // No pre-flight probe — the file endpoint doesn't allow HEAD so a
            // 405 would wrongly reject valid uploads. Components that render
            // the logo should wrap the <img> with `onError={brandingFallback}`
            // so a broken file degrades gracefully to the local monogram.
            setBranding({
                institution_name:
                    data.institution_name || "Heritage Community College eLearning",
                tagline: data.tagline || "Learn Anywhere. Grow Everywhere.",
                logo_url: resolveLogo(data.logo_url) || LOCAL_MONOGRAM,
            });
        } catch {
            // keep defaults (monogram)
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    // Sync the browser tab title to whatever the admin has set as the
    // institution name. The favicon stays as the static SVG monogram
    // (public/favicon.svg) — uploaded PNG logos look muddy at 16×16 and
    // any glitch with the file endpoint would leave us with no icon at all.
    useEffect(() => {
        try {
            if (branding.institution_name) {
                const base = branding.institution_name.trim();
                const endsWithLearning = /e[\s-]?learning$/i.test(base);
                document.title = endsWithLearning
                    ? base
                    : `${base} — eLearning`;
            }
        } catch {
            // ignore — title update is non-critical
        }
    }, [branding.institution_name]);

    return (
        <BrandingCtx.Provider value={{ ...branding, refresh }}>
            {children}
        </BrandingCtx.Provider>
    );
}

export function resolveLogo(logoUrl) {
    if (!logoUrl) return null;
    if (logoUrl.startsWith("http")) return logoUrl;
    // Relative API path (e.g. /api/files/public/<id>)
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    return `${backend}${logoUrl}`;
}

/**
 * Drop-in <img onError> handler for any element that renders the brand
 * logo. Gracefully falls back to the local SVG monogram if the uploaded
 * file is missing/corrupt/tiny so the user never sees a broken-image icon.
 */
export function brandingFallback(e) {
    if (e?.currentTarget && e.currentTarget.src !== window.location.origin + LOCAL_MONOGRAM) {
        e.currentTarget.src = LOCAL_MONOGRAM;
    }
}

export const useBranding = () => useContext(BrandingCtx);
